import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import toast from 'shared/utils/toast';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { productService } from 'shared/services';
import { supplierTransportDocumentActiveLineService } from '../../SupplierTrasportActiveDocument/api/supplier-transport-active-document-line.service';
import { supplierTransportActiveDocumentService } from '../../SupplierTrasportActiveDocument/api/supplier-transport-active-document.service';
import { transportDocumentActiveLineService } from '../api/transport-active-document-line.service';
import { transportActiveDocumentService } from '../api/transport-active-document.service';

/**
 * Custom React hook that encapsulates the state management, business logic,
 * and API side-effects for active transport documents (DDT / Documento di Trasporto).
 *
 * This hook dynamically toggles between standard client-facing documents and
 * supplier-facing documents based on the `isSupplierDdt` flag.
 *
 * @param {boolean} isSupplierDdt - Flag identifying whether to fetch and mutate supplier-specific DDT data.
 * @param {string} translationPrefix - Namespace prefix used to fetch i18next translation keys dynamically.
 *
 * @returns {Object} An object containing all isolated states, dialog toggles, and data mutation actions.
 * @property {string | undefined} id - The current active document unique identifier extracted from the URL route parameter.
 * @property {any} ddt - The primary entity record containing the document profile, header information, and nested item lines.
 * @property {React.Dispatch<any>} setDdt - Local React state modifier function exposed to enable optimistic state additions or updates on the active document array.
 * @property {any[]} productOptions - A list of catalog products fetched asynchronously and available to add as new rows.
 * @property {React.Dispatch<any>} setProductToAdd - State trigger used to assign a chosen product node before structural line creation.
 * @property {boolean} isDisabledEdit - Security rule indicator evaluating true if the logged user holds restricted Technician permissions.
 * @property {boolean} isClosed - Status flag determining if the active document is locked for data modifications (e.g., status is 'SENT').
 * @property {boolean} isGenerating - Loading state tracking the background asynchronous execution of transient PDF document rendering.
 * @property {boolean} confirmFinalizeDialog - Visibility flag managing the confirmation modal overlay prior to closing a document state.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setConfirmFinalizeDialog - Modal visibility dispatcher.
 * @property {() => boolean} changesToSave - Evaluator function checking whether modifications exist by running a deep equality match against the original baseline data model.
 * @property {(values: any) => Promise<void>} updateDDT - Persistence function that sanitizes relational payload fields and triggers header metadata updates.
 * @property {() => Promise<void>} handleTmpDdt - Visual exporter action that generates and triggers downloads for the layout snapshot.
 * @property {() => Promise<void>} finalizeDdt - Action workflow converting document status into a closed state, displaying safety exceptions or early-expiration warnings if present.
 * @property {(lineId: number | null) => Promise<void>} deleteLine - Destructive handler sending delete actions to the line microservice and refreshing the dataset view.
 * @property {() => Promise<void>} saveBulkLines - Synchronous pipeline dispatcher mapping temporary UI rows into real relational database entities.
 * @property {() => Promise<void>} getDDTById - Explicit data refresh engine used to pull matching remote records down into local components.
 */
export function useTransportActiveDocument(isSupplierDdt: boolean, translationPrefix: string) {
  const { t } = useTranslation();
  const { id } = useParams();

  const activeDocumentService = isSupplierDdt
    ? supplierTransportActiveDocumentService
    : transportActiveDocumentService;

  const activeDocumentLineService = isSupplierDdt
    ? supplierTransportDocumentActiveLineService
    : transportDocumentActiveLineService;

  const [ddt, setDdt] = useState<any>(null);
  const [initialDdtLines, setInitialDdtLines] = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [productToAdd, setProductToAdd] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [confirmFinalizeDialog, setConfirmFinalizeDialog] = useState<boolean>(false);

  const isDisabledEdit = useIsTechnician();
  const isClosed = ddt?.status === 'SENT';

  /**
   * Fetches the complete document record matching the contextual route ID parameter
   * and sets up baseline reference boundaries to catch unstaged UI alterations.
   */
  const getDDTById = async () => {
    try {
      const res = await activeDocumentService.get(Number(id));
      setDdt(res);
      setInitialDdtLines(res?.lines || []);
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Dispatches catalog product queries using custom warehouse parameters or supplier identification codes
   * depending on the operational scope of the active document.
   *
   * @param {string} [ref] - Optional target production order override reference code.
   */
  const getProductSummary = async (ref?: string) => {
    if (!ddt) return;
    try {
      const params = !isSupplierDdt
        ? {
            warehouse_id: ddt.warehouse_out,
            status: 'ACTIVE',
            order: ref || ddt.order_ref || '',
            has_orders: !!(ref || ddt.order_ref)
          }
        : { status: 'ACTIVE', supplier: ddt.supplier };

      const res = await productService.getAllBaseInformation(params);
      setProductOptions(res);
    } catch (err) {
      console.error(err);
    }
  };

  /** Sets up the initial loading lifecycle whenever the route document identification key shifts */
  useEffect(() => {
    getDDTById();
  }, [id]);

  /** Keeps inventory items synchronized with state adjustments or contextual field mutations */
  useEffect(() => {
    if (ddt?.id) {
      getProductSummary();
    }
  }, [ddt?.id, productToAdd?.id]);

  /**
   * Verifies if local staging alterations have taken place by doing a serialized string mismatch test.
   * @returns {boolean} True if local document rows do not match original fetched rows.
   */
  const changesToSave = (): boolean => {
    if (!ddt) return false;
    return JSON.stringify(initialDdtLines) !== JSON.stringify(ddt.lines);
  };

  /**
   * Validates structure formatting and pushes structural master updates back to the API.
   * @param {any} values - Component form attributes payload mapping incoming metadata properties.
   */
  const updateDDT = async (values: any) => {
    if (!ddt?.id) return;
    try {
      const res = await activeDocumentService.update(ddt.id, {
        ...values,
        warehouse_out:
          typeof values.warehouse_out === 'object'
            ? values.warehouse_out['id']
            : values.warehouse_out,
        warehouse_in:
          typeof values.warehouse_in === 'object' ? values.warehouse_in['id'] : values.warehouse_in
      });
      if (res) {
        if ('warnings' in res) {
          res.warnings.forEach((w: string) => toast.warning(w));
        } else {
          toast.success(t(`${translationPrefix}.updated`));
        }
        await getDDTById();
        getProductSummary(res.order);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /** Triggers draft document printing actions and automatically hooks file rendering into local streams */
  const handleTmpDdt = async () => {
    if (!ddt?.id) return;
    setIsGenerating(true);
    try {
      await activeDocumentService.generateTmpPdf(ddt.id);
      toast.success(t(`${translationPrefix}.pdf-tmp-generated`));
      activeDocumentService.downloadTmpPdf(ddt.id);
    } catch (err) {
      toast.error(t(`${translationPrefix}.generation-failed`));
    } finally {
      setIsGenerating(false);
    }
  };

  /** Finalizes a workflow, processes business constraints, and logs out-of-date product warning sequences */
  const finalizeDdt = async () => {
    if (!ddt?.id) return;
    try {
      const res = await activeDocumentService.finalize(ddt.id);
      if (res?.warnings?.length) {
        res.warnings.forEach((w: any) => {
          w?.batches
            ? toast.warning(t('alerts.earlier-expiration', { batches: w.batches.join(', ') }))
            : toast.warning(w.type);
        });
      } else if (res?.error) {
        toast.error(res.error);
      } else {
        toast.success(t(`${translationPrefix}.finalized`));
      }
      getDDTById();
    } catch (err) {
      console.error(err);
    } finally {
      setConfirmFinalizeDialog(false);
    }
  };

  /**
   * Drops a target active line row using backend endpoints and re-runs the verification payload logic.
   * @param {number | null} lineId - Primary identifier key of the specific target item row.
   */
  const deleteLine = async (lineId: number | null) => {
    if (!lineId) return;
    try {
      const res = await activeDocumentLineService.delete(lineId);
      if (res?.status === 204) {
        toast.success(t(`${translationPrefix}.line-deleted-success`));
      } else {
        toast.error(t(`${translationPrefix}.line-deleted-error`));
      }
      getDDTById();
    } catch (err) {
      toast.error(t(`${translationPrefix}.line-deleted-error`));
    }
  };

  /** Bundles all staged rows and commits them sequentially via bulk operations API endpoints */
  const saveBulkLines = async () => {
    if (!ddt?.id) return;
    try {
      const res = await activeDocumentLineService.bulkCreate(ddt.id, ddt.lines);
      if (res.status === 201) {
        res.data?.warnings
          ? res.data.warnings.forEach((w: string) => toast.warning(w))
          : toast.success(res.data.detail);
      } else {
        toast.error(t(`${translationPrefix}.line-add-error`));
      }
      getDDTById();
    } catch (err) {
      toast.error(t(`${translationPrefix}.line-add-error`));
    }
  };

  return {
    id,
    ddt,
    setDdt,
    productOptions,
    setProductToAdd,
    isDisabledEdit,
    isClosed,
    isGenerating,
    confirmFinalizeDialog,
    setConfirmFinalizeDialog,
    changesToSave,
    updateDDT,
    handleTmpDdt,
    finalizeDdt,
    deleteLine,
    saveBulkLines,
    getDDTById
  };
}

//   const { t } = useTranslation();
//   const { id } = useParams();
//   const activeDocumentService = isSupplierDdt
//     ? supplierTransportActiveDocumentService
//     : transportActiveDocumentService;

//   const [ddt, setDdt] = useState<ITransportActiveDocument>();
//   const [initialDdtLines, setInitialDdtLines] = useState<any>();

//   const [productOptions, setProductOptions] = useState<IOption[]>([]);
//   const [productToAdd, setProductToAdd] = useState<any>(null);
//   const [isClosed, setIsClosed] = useState<boolean>(false);

//   const isDisabledEdit = isTechnician();

//   const [confirmFinalizeDialog, setConfirmFinalizeDialog] = useState(false);

//   // ----------------------------------------------------------- GETTER AND USEFFECTS

//   const getDDTById = () => {
//     activeDocumentService.get(Number(id)).then((res) => {
//       setDdt(res);
//       if (res.status === 'SENT') {
//         setIsClosed(true);
//       }
//       setInitialDdtLines(res.lines);
//     });
//   };

//   const getProductSummary = (ref?: string) => {
//     if (!isSupplierDdt) {
//       const warehouse_params = {
//         warehouse_id: ddt.warehouse_out,
//         // supplier: isSupplierDdt ? ddt.supplier : undefined, // commented out as it may not be needed
//         status: 'ACTIVE',
//         order: ref ? ref : ddt.order_ref ? ddt.order_ref : '',
//         has_orders: ref ? true : ddt.order_ref ? true : false
//       };
//       productService.getAllBaseInformation(warehouse_params).then(setProductOptions);
//     } else {
//       const supplier_params: any = {
//         // supplier: isSupplierDdt ? ddt.supplier : undefined, // commented out as it may not be needed
//         status: 'ACTIVE',
//         supplier: ddt.supplier
//       };
//       productService.getAllBaseInformation(supplier_params).then(setProductOptions);
//     }
//   };

//   useEffect(() => {
//     getDDTById();
//   }, []);

//   useEffect(() => {
//     if (ddt && ddt.id) {
//       getProductSummary();
//     }
//   }, [ddt?.id, productToAdd?.id]);

//   const changesToSave = (): boolean => {
//     return JSON.stringify(initialDdtLines) === JSON.stringify(ddt.lines);
//   };

//   // ---------------------------------------------------------- UPDATE-TRANSPORT-ACTIVE-DOCUMENT
//   const updateDDT = (values: any) => {
//     activeDocumentService
//       .update(ddt.id, {
//         ...values,
//         warehouse_out:
//           typeof values.warehouse_out === 'object'
//             ? values.warehouse_out['id']
//             : values.warehouse_out,
//         warehouse_in:
//           typeof values.warehouse_in === 'object' ? values.warehouse_in['id'] : values.warehouse_in
//       })
//       .then((res) => {
//         if (res) {
//           if ('warnings' in res) {
//             res.warnings.map((warning) => {
//               toast.warning(warning);
//             });
//           } else {
//             toast.success(t(`${translationPrefix}.updated`));
//           }
//           getDDTById();
//           getProductSummary(res.order);
//         }
//       });
//   };

//   // ---------------------------------------------------------- GENERATE-TMP-DOCUMENT

//   const [isGenerating, setIsGenerating] = useState(false);

//   const handleTmpDdt = () => {
//     setIsGenerating(true); // Attiva il loading

//     activeDocumentService
//       .generateTmpPdf(ddt.id)
//       .then(() => {
//         toast.success(t(`${translationPrefix}.pdf-tmp-generated`));
//         activeDocumentService.downloadTmpPdf(ddt.id);
//       })
//       .catch((error) => {
//         console.error(error);
//         toast.error(t(`${translationPrefix}.generation-failed`));
//       })
//       .finally(() => {
//         setIsGenerating(false); // Disattiva il loading in ogni caso
//       });
//   };

//   // ---------------------------------------------------------- CONFIRM-FINALIZE

//   const openConfirmfinalizeDialog = () => {
//     setConfirmFinalizeDialog(true);
//   };

//   const closeConfirmfinalizeDialog = () => {
//     setConfirmFinalizeDialog(false);
//   };

//   const finalizeDdt = () => {
//     activeDocumentService.finalize(ddt.id).then((res) => {
//       if (res?.warnings?.length) {
//         res.warnings.forEach((warning) => {
//           if (warning?.batches) {
//             toast.warning(t('alerts.earlier-expiration', { batches: warning.batches.join(', ') }));
//           } else {
//             toast.warning(warning.type);
//           }
//         });
//       } else if (res?.error) {
//         toast.error(res.error);
//       } else {
//         toast.success(t(`${translationPrefix}.finalized`));
//       }
//       getDDTById();
//     });
//     closeConfirmfinalizeDialog();
//   };

// const activeDocumentLineService = isSupplierDdt
//     ? supplierTransportDocumentActiveLineService
//     : transportDocumentActiveLineService;

//   const [ddt, setDdt] = useState<ITransportActiveDocument>();
//   const [initialDdtLines, setInitialDdtLines] = useState<any>();

//   const [productOptions, setProductOptions] = useState<IOption[]>([]);
//   const [productToAdd, setProductToAdd] = useState<any>(null);
//   const [isClosed, setIsClosed] = useState<boolean>(false);

//   const [isStatusNew, _] = useState<boolean>(true);

//   const isDisabledEdit = isTechnician();
//   const [selectedRow, setSelectedRow] = useState(null);
//   const [infoOpen, setInfoOpen] = useState(false);

//   // ----------------------------------------------------------- GETTER AND USEFFECTS
//   const handleOpenInfoDialog = (row) => {
//     setSelectedRow(row);
//     setInfoOpen(true);
//   };

//   const handleCloseInfoDialog = () => {
//     setInfoOpen(false);
//     setSelectedRow(null);
//   };

//   // ---------------------------------------------------------- TRASFERIMENTO-LINE-LOGIC
//   const [editTransportActiveDocumentLineRow, setEditTransportActiveDocumentLineRow] =
//     useState<any>(null);
//   const [openEditDialog, setOpenEditDialog] = useState(false);

//   // ---------------------------------------------------------  EDIT LINE
//   const setOpenDialog = (row: any) => {
//     setEditTransportActiveDocumentLineRow(row);
//     setOpenEditDialog(true);
//   };

//   const setCloseDialog = () => {
//     setOpenEditDialog(false);
//   };

//   const onSubmitEdit = async (id, params) => {
//     activeDocumentLineService.update(id, params).then(() => {
//       toast.success('Updated successfully');
//     });
//     const updatedLines = ddt.lines.map((line) => {
//       if (line.id === id) {
//         return { ...line, ...params };
//       }
//       return line;
//     });
//     setDdt({ ...ddt, lines: updatedLines });
//     setCloseDialog();
//   };

//   // ---------------------------------------------------------- DELETE-LINE

//   const handleDelete = (id: number | null) => {
//     if (changesToSave()) {
//       activeDocumentLineService.delete(id).then((res) => {
//         if (res && res.status === 204) {
//           toast.success(t(`${translationPrefix}.line-deleted-success`));
//         } else {
//           toast.error(t(`${translationPrefix}.line-deleted-error`));
//         }
//       });
//     }
//     getDDTById();
//   };

//   const saveDDTLines = () => {
//     activeDocumentLineService.bulkCreate(ddt.id, ddt.lines).then((res) => {
//       if (res.status === 201) {
//         if (res.data?.warnings) {
//           res.data.warnings.forEach((warning: string) => toast.warning(warning));
//         } else {
//           toast.success(res.data.detail);
//         }
//       } else {
//         toast.error(t(`${translationPrefix}.line-add-error`));
//       }
//     });
//     setInitialDdtLines(ddt.lines);
//     setTimeout(() => {
//       getDDTById();
//     }, 3000);
//   };

//   const columns: GridColDef[] = TransportActiveDocumentLinesColumns(
//     isStatusNew,
//     handleDelete,
//     setOpenDialog,
//     handleOpenInfoDialog,
//     isDisabledEdit,
//     isClosed,
//     isSupplierDdt
//   );

//   const onSubmitRow = (values: any) => {
//     if (values.quantity > 0) {
//       const product_batch_code = values['product_batch'].product_batch ?? 'NO_BATCH';
//       const expiration_date = values['product_batch'].expiration_date ?? '';
//       const location_code = values['product_batch']?.location ?? '';
//       values['product_code'] = values['product'].code;
//       values['product_description'] = values['product'].description.replace(
//         //correzione descrizion IOption
//         values['product'].code + ' - ',
//         ''
//       );
//       values['expiration_date'] = expiration_date;
//       values['location'] = location_code;
//       values['product_batch'] = product_batch_code;
//       values['document'] = ddt.id;
//       values['notes'] = location_code;
//       values['id'] = uuidv4();
//       const updatedLines = [...ddt.lines, values];
//       setDdt({ ...ddt, lines: updatedLines });
//     } else {
//       toast.warning(t(`${translationPrefix}`));
//     }
//   };
